/**
 * Continuity Import — parses and validates a ContinuitySnapshot JSON string.
 * (Participant matching happens by exact name at the consumption sites:
 * `buildPhantomContext` in the engine and `day0-adapter` in the web layer.)
 */

import type { ContinuitySnapshot } from '../models/continuity-schema';
import { REST_RULE_MAX_HOURS } from '../shared/utils/rest-rule-clamp';
import { sanitizeColor } from './certification-utils';

// ─── Parse & Validate ───────────────────────────────────────────────────────

/**
 * Parse a JSON string into a validated ContinuitySnapshot.
 * Returns the snapshot on success, or an object with an `error` message.
 */
export function parseContinuitySnapshot(json: string): ContinuitySnapshot | { error: string } {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return { error: 'JSON לא תקין — בדוק את הפורמט.' };
  }

  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { error: 'הנתון חייב להיות אובייקט JSON.' };
  }

  const obj = raw as Record<string, unknown>;

  // Schema version check
  if (obj.schemaVersion !== 1) {
    return { error: `גרסת קובץ לא נתמכת: ${obj.schemaVersion ?? 'חסר'}. נדרשת גרסה 1.` };
  }

  // Required top-level fields
  if (typeof obj.exportedAt !== 'string') {
    return { error: 'שדה "exportedAt" חסר או לא תקין.' };
  }
  if (typeof obj.dayIndex !== 'number' || obj.dayIndex < 1) {
    return { error: 'שדה "dayIndex" חסר או לא תקין (חייב להיות מספר חיובי).' };
  }

  // Day window
  const dw = obj.dayWindow as Record<string, unknown> | undefined;
  if (!dw || typeof dw.start !== 'string' || typeof dw.end !== 'string') {
    return { error: 'שדה "dayWindow" חסר או לא תקין.' };
  }
  if (isNaN(Date.parse(dw.start as string)) || isNaN(Date.parse(dw.end as string))) {
    return { error: 'תאריכים ב-"dayWindow" אינם תקינים.' };
  }

  // Participants array
  if (!Array.isArray(obj.participants)) {
    return { error: 'שדה "participants" חסר או לא תקין (חייב להיות מערך).' };
  }

  for (let i = 0; i < (obj.participants as unknown[]).length; i++) {
    const p = (obj.participants as unknown[])[i];
    const err = validateParticipantEntry(p, i);
    if (err) return { error: err };
  }

  const snapshot = obj as unknown as ContinuitySnapshot;
  // Coerce every assignment color to a safe hex literal at this import
  // chokepoint (the same posture as cert/template imports), so the raw
  // `style="…"` Day-0 render sinks can interpolate `color` directly.
  sanitizeContinuitySnapshotColors(snapshot);
  return snapshot;
}

/**
 * Coerce every assignment `color` in a continuity snapshot to a safe hex literal,
 * in place. Untrusted snapshots (pasted/imported/persisted) may carry a `color`
 * crafted to break out of a `style="…"` attribute at a Day-0 render sink
 * (`day0-adapter` grid section tint, profile Day-0 agenda card); routing it
 * through the same hex guard the cert/template import boundaries use keeps those
 * sinks safe. Absent colors are left untouched so each renderer's own neutral
 * fallback still applies; a present-but-malformed color collapses to that same
 * neutral grey.
 */
export function sanitizeContinuitySnapshotColors(snap: ContinuitySnapshot): void {
  for (const p of snap.participants ?? []) {
    for (const a of p.assignments ?? []) {
      if (typeof a.color === 'string') a.color = sanitizeColor(a.color, '#95A5A6');
    }
  }
}

// ─── Validation Helpers ─────────────────────────────────────────────────────

function validateParticipantEntry(p: unknown, index: number): string | null {
  if (typeof p !== 'object' || p === null || Array.isArray(p)) {
    return `משתתף #${index + 1}: חייב להיות אובייקט.`;
  }
  const obj = p as Record<string, unknown>;

  if (typeof obj.name !== 'string' || obj.name.trim() === '') {
    return `משתתף #${index + 1}: שדה "name" חסר או ריק.`;
  }
  if (typeof obj.level !== 'number') {
    return `משתתף "${obj.name ?? index + 1}": שדה "level" חסר או לא תקין.`;
  }
  if (!Array.isArray(obj.certifications)) {
    return `משתתף "${obj.name}": שדה "certifications" חסר או לא תקין.`;
  }
  if (typeof obj.group !== 'string') {
    return `משתתף "${obj.name}": שדה "group" חסר או לא תקין.`;
  }
  if (!Array.isArray(obj.assignments)) {
    return `משתתף "${obj.name}": שדה "assignments" חסר או לא תקין.`;
  }

  for (let j = 0; j < (obj.assignments as unknown[]).length; j++) {
    const err = validateAssignmentEntry((obj.assignments as unknown[])[j], obj.name as string, j);
    if (err) return err;
  }

  return null;
}

function validateAssignmentEntry(a: unknown, participantName: string, index: number): string | null {
  if (typeof a !== 'object' || a === null || Array.isArray(a)) {
    return `${participantName}, שיבוץ #${index + 1}: חייב להיות אובייקט.`;
  }
  const obj = a as Record<string, unknown>;

  if (typeof obj.sourceName !== 'string' && typeof obj.taskType !== 'string') {
    return `${participantName}, שיבוץ #${index + 1}: שדה "sourceName" חסר.`;
  }
  if (typeof obj.taskName !== 'string') {
    return `${participantName}, שיבוץ #${index + 1}: שדה "taskName" חסר.`;
  }

  // timeBlock
  const tb = obj.timeBlock as Record<string, unknown> | undefined;
  if (!tb || typeof tb.start !== 'string' || typeof tb.end !== 'string') {
    return `${participantName}, שיבוץ #${index + 1}: שדה "timeBlock" חסר או לא תקין.`;
  }
  if (isNaN(Date.parse(tb.start as string)) || isNaN(Date.parse(tb.end as string))) {
    return `${participantName}, שיבוץ #${index + 1}: תאריכים ב-"timeBlock" אינם תקינים.`;
  }
  // The interval must be strictly ordered. An inverted (or zero-length) block is
  // an EMPTY span, and every cross-boundary hard constraint silently fails open
  // on it: HC-12's `candidate.start − phantom.end` turns positive, HC-14's
  // `Math.max(start − otherEnd, otherStart − end)` can only grow, and HC-15's
  // recovery window — anchored on `timeBlock.end` — opens before the shift even
  // begins while `hasLoadDuringOverlap` bails on the empty overlap. The failure
  // is invisible: phantoms never reach `schedule.violations`, so the user sees a
  // green "✓ N משתתפים" chip and is then scheduled straight onto the shift after
  // the night they just worked — exactly what continuity exists to prevent.
  // Rejecting is safe: no producer can emit this (`exportDaySnapshot` copies a
  // `Task` built as `start + strictly-positive ms`, and every bundled
  // default-continuity offset pair is ordered).
  if (Date.parse(tb.end as string) <= Date.parse(tb.start as string)) {
    return `${participantName}, שיבוץ #${index + 1}: זמן הסיום ב-"timeBlock" חייב להיות אחרי זמן ההתחלה.`;
  }

  if (typeof obj.blocksConsecutive !== 'boolean') {
    return `${participantName}, שיבוץ #${index + 1}: שדה "blocksConsecutive" חסר.`;
  }
  // restRuleId is optional (string or absent)
  if (obj.restRuleId !== undefined && typeof obj.restRuleId !== 'string') {
    return `${participantName}, שיבוץ #${index + 1}: שדה "restRuleId" לא תקין.`;
  }
  // restRuleDurationHours is optional; if present it must be a finite number in
  // (0, REST_RULE_MAX_HOURS]. HC-14's only test is `gap < durationMs`, so BOTH
  // ends are load-bearing and fail silently in opposite directions:
  //   - non-numeric (NaN) / zero / negative ⇒ the comparison is always false, so
  //     HC-14 is silently DISABLED across the schedule boundary;
  //   - above the catalog ceiling ⇒ always true, so every rest-ruled pair is
  //     flagged and the participant is locked out of every rest-ruled placement.
  // The over-blocking direction is not confined to phantom pairs: `mergePhantomRules`
  // deliberately merges a rule id the live catalog does not know, and a live template
  // may carry exactly such an id (the `ORPHAN_REST_RULE` preflight state), at which
  // point `checkRestRules` arms HC-14 for REAL tasks using this duration — and its
  // same-rule phase has no `Math.min` bound. This mirrors the sibling
  // `sleepRecovery.recoveryHours` bound below, for the same stated reason.
  //
  // Rejecting the ceiling is safe: no producer can emit an out-of-range value.
  // `addRestRule`/`updateRestRule` clamp to [0.5, 24] and the task-rules input
  // carries min="0.5" max="24"; both `exportDaySnapshot` call sites pass a map
  // pre-clamped through `sanitizeRestRuleSnapshot`; and the clamp shipped in the
  // same commit that introduced rest rules at all, so no legacy snapshot can carry
  // a larger value. That matters because `doGenerateFromDay` is a mainline
  // self-produce → re-import flow a false rejection would break. The ceiling must
  // stay exactly REST_RULE_MAX_HOURS so this validator and the engine's
  // `buildPhantomContext` clamp agree. No `< 0.5` arm: the engine clamp normalizes
  // small-but-positive values and nothing depends on rejecting them.
  if (
    obj.restRuleDurationHours !== undefined &&
    (typeof obj.restRuleDurationHours !== 'number' ||
      !Number.isFinite(obj.restRuleDurationHours) ||
      obj.restRuleDurationHours <= 0 ||
      obj.restRuleDurationHours > REST_RULE_MAX_HOURS)
  ) {
    return `${participantName}, שיבוץ #${index + 1}: שדה "restRuleDurationHours" לא תקין.`;
  }
  // baseLoadWeight is optional; if present it must be a FINITE number.
  // Range is deliberately NOT bounded here: `clamp01` at both engine read sites
  // (`getTaskBaseLoadWeight` / `getLoadWeightAtTime`) already pins every finite
  // value into [0, 1], and `addTaskTemplate` accepts an unclamped value, so a
  // template carrying an out-of-range weight can legitimately round-trip through
  // `exportDaySnapshot` — a [0, 1] reject would refuse a self-produced snapshot.
  // A NON-numeric value is the live vector: `Math.min`/`Math.max` coerce, so it
  // survives clamp01 as NaN, and NaN fails HC-15's `weight > 0` test open.
  // Note this is type hygiene, not a gate — unlike `restRuleDurationHours`, a
  // perfectly legal `0` produces the identical fail-open.
  if (
    obj.baseLoadWeight !== undefined &&
    (typeof obj.baseLoadWeight !== 'number' || !Number.isFinite(obj.baseLoadWeight))
  ) {
    return `${participantName}, שיבוץ #${index + 1}: שדה "baseLoadWeight" לא תקין.`;
  }
  // loadWindows is optional; if present it must be an array of well-formed windows.
  // A malformed window (e.g. a non-numeric startHour) silently makes HC-12's
  // boundary-blocking check fail open across the schedule boundary.
  if (obj.loadWindows !== undefined) {
    if (!Array.isArray(obj.loadWindows)) {
      return `${participantName}, שיבוץ #${index + 1}: שדה "loadWindows" חייב להיות מערך.`;
    }
    for (let w = 0; w < (obj.loadWindows as unknown[]).length; w++) {
      const err = validateLoadWindowEntry((obj.loadWindows as unknown[])[w], participantName, index, w);
      if (err) return err;
    }
  }
  // shiftIndex is optional; if present it must be a 1-based positive integer.
  // It gates whether `sleepRecovery` triggers (getRecoveryWindow compares it
  // against the rule's triggerShifts), so a bad value would mis-gate HC-15.
  if (obj.shiftIndex !== undefined && (!Number.isInteger(obj.shiftIndex) || (obj.shiftIndex as number) < 1)) {
    return `${participantName}, שיבוץ #${index + 1}: שדה "shiftIndex" לא תקין.`;
  }
  // sleepRecovery is optional; if present it must be a well-formed HC-15 rule:
  // a non-empty triggerShifts array of positive integers and a finite
  // recoveryHours in [1, 48]. A malformed rule could otherwise seed a phantom
  // recovery window that is absent (no cross-boundary enforcement) or
  // effectively infinite (over-blocking) — bound it at the import boundary.
  if (obj.sleepRecovery !== undefined) {
    const sr = obj.sleepRecovery;
    if (typeof sr !== 'object' || sr === null || Array.isArray(sr)) {
      return `${participantName}, שיבוץ #${index + 1}: שדה "sleepRecovery" לא תקין.`;
    }
    const srObj = sr as Record<string, unknown>;
    const triggers = srObj.triggerShifts;
    if (
      !Array.isArray(triggers) ||
      triggers.length === 0 ||
      !triggers.every((s) => Number.isInteger(s) && (s as number) >= 1)
    ) {
      return `${participantName}, שיבוץ #${index + 1}: שדה "sleepRecovery.triggerShifts" לא תקין.`;
    }
    if (
      typeof srObj.recoveryHours !== 'number' ||
      !Number.isFinite(srObj.recoveryHours) ||
      srObj.recoveryHours < 1 ||
      srObj.recoveryHours > 48
    ) {
      return `${participantName}, שיבוץ #${index + 1}: שדה "sleepRecovery.recoveryHours" לא תקין.`;
    }
  }
  return null;
}

/**
 * Validate a single load-window entry from a continuity assignment. Fields mirror
 * `ContinuityLoadWindow`. Hours/minutes must be in-range integers; weight a finite
 * number in [0, 1] (0 and 1 are both legitimate). Returns a Hebrew error string on
 * the first violation, or null when the window is well-formed.
 */
function validateLoadWindowEntry(
  w: unknown,
  participantName: string,
  asgnIndex: number,
  windowIndex: number,
): string | null {
  const where = `${participantName}, שיבוץ #${asgnIndex + 1}, חלון #${windowIndex + 1}`;
  if (typeof w !== 'object' || w === null || Array.isArray(w)) {
    return `${where}: חייב להיות אובייקט.`;
  }
  const o = w as Record<string, unknown>;

  if (typeof o.id !== 'string' || o.id.trim() === '') {
    return `${where}: שדה "id" חסר או ריק.`;
  }
  if (!Number.isInteger(o.startHour) || (o.startHour as number) < 0 || (o.startHour as number) > 23) {
    return `${where}: שדה "startHour" לא תקין.`;
  }
  if (!Number.isInteger(o.startMinute) || (o.startMinute as number) < 0 || (o.startMinute as number) > 59) {
    return `${where}: שדה "startMinute" לא תקין.`;
  }
  if (!Number.isInteger(o.endHour) || (o.endHour as number) < 0 || (o.endHour as number) > 23) {
    return `${where}: שדה "endHour" לא תקין.`;
  }
  if (!Number.isInteger(o.endMinute) || (o.endMinute as number) < 0 || (o.endMinute as number) > 59) {
    return `${where}: שדה "endMinute" לא תקין.`;
  }
  if (typeof o.weight !== 'number' || !Number.isFinite(o.weight) || o.weight < 0 || o.weight > 1) {
    return `${where}: שדה "weight" לא תקין.`;
  }
  if (o.blocksAtBoundary !== undefined && typeof o.blocksAtBoundary !== 'boolean') {
    return `${where}: שדה "blocksAtBoundary" לא תקין.`;
  }
  return null;
}
